import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

/**
 * Capability booleans for the Teacher card, derived from the current user's
 * roles. Mirrors `useStudentPermissions`. Hiding a control is a UX affordance
 * only — the backend remains the authority on each mutation.
 */
export interface ITeacherPermissions {
  /** Edit the teacher record + append applications + profile fields. */
  canManageTeacher: boolean;
  /** Assign to a group via the «Заявка» flow. */
  canAddApplication: boolean;
  /** Edit the profile fields (rating / note / courses). */
  canEditProfile: boolean;
  /** Write an email to the teacher. */
  canContactEmail: boolean;
  /** Link / unlink PandaDoc contracts. */
  canManagePandadoc: boolean;
}

// Mirror the BACKEND role gates exactly — a mismatch shows a control the
// backend then denies (silent failure / misleading empty state).
// Teacher schema (query + mutations incl. setTeacherProfile / applications):
const TEACHER_ROLES = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT, // AccountManager
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
];
// PandaDoc schema (pandadocDocuments + link/unlink) — NB: HRManager is NOT
// allowed here, but FinanceManager is.
const PANDADOC_ROLES = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER, // FinanceManager
];

const hasAnyRole = (roles: UserRole[], allowed: UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

export const useTeacherPermissions = (): ITeacherPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];

  const canManageTeacher = hasAnyRole(roles, TEACHER_ROLES);

  return {
    canManageTeacher,
    canAddApplication: canManageTeacher,
    canEditProfile: canManageTeacher,
    canContactEmail: canManageTeacher,
    canManagePandadoc: hasAnyRole(roles, PANDADOC_ROLES),
  };
};
